import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportForm, PreviewData } from 'routes/Cellar/types';
import SubmitModal from 'components/Submits/SubmitModal';
import { trackEvent } from 'helpers/trackerHelpers';
import PreVizHeader from './components/PreVizHeader';
import PreVizFooter from './components/PreVizFooter';
import { Action } from './components/ActionButtonsGroup';
import { useMalformattedPrevizData } from './hooks/malformattedData';
import { includesGeoColumns } from './helpers/geoColumns';
import PreviewTable from 'routes/Cellar/components/Preview/PreviewTable/PreviewTable';

type Props = {
    previzData: PreviewData;
    importFile: (arg1: Action) => void;
    cancel: () => void;
    isNewTable: boolean;
    isTableUpdateInProgress: boolean;
    tableColumns: ResponseColumnT[] | undefined;
    importNewTableFile: (tableName: string) => void;
    setImportForm: (updater: (importForm: ImportForm) => ImportForm) => void;
};

const PreVizTable = ({
    previzData,
    importFile,
    cancel,
    isNewTable,
    isTableUpdateInProgress,
    tableColumns,
    importNewTableFile,
    setImportForm,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [action, setAction] = useState<Action>('append');
    const [columns, setColumns] = useState(previzData.columns);
    const [newTableName, setNewTableName] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [showMalformatted, setShowMalformatted] = useState(false);
    const [hasMalformattedRows, setHasMalformattedRows] = useState(false);
    const [openMalformattedModal, setOpenMalformattedModal] = useState(false);

    const getMalformattedPrevizData = useMalformattedPrevizData(
        columns,
        previzData,
        isNewTable,
        // TODO StrictNullCheck : Try to remove "!"
        tableColumns!,
    );
    const malformattedPrevizData = getMalformattedPrevizData();
    useEffect(
        () => setHasMalformattedRows(malformattedPrevizData.nbRows > 0),
        [malformattedPrevizData.nbRows],
    );

    useEffect(
        () =>
            setImportForm((prevState: ImportForm) => ({
                ...prevState,
                columns,
                isPrivate,
            })),
        [setImportForm, isPrivate, columns],
    );

    const onImportClick = () => {
        if (isNewTable) {
            importNewTableFile(newTableName);
        } else {
            importFile(action);
        }
    };
    const onSubmit = () => {
        trackEvent(
            isNewTable
                ? 'settings/data/csv-imports-management/nouvelle-table/csv-imported-succesfully'
                : 'settings/data/csv-imports-management/existing-table/data-added-successfully',
        );
        if (hasMalformattedRows) {
            setOpenMalformattedModal(true);
        } else {
            onImportClick();
        }
    };

    return (
        <>
            <PreVizHeader
                isNewTable={isNewTable}
                malformattedPrevizData={malformattedPrevizData}
                newTableName={newTableName}
                previzData={previzData}
                setNewTableName={setNewTableName}
                setShowMalformatted={setShowMalformatted}
                showMalformatted={showMalformatted}
                showGeoTypeAlert={
                    includesGeoColumns(columns) ||
                    includesGeoColumns(tableColumns || [])
                }
            />
            <PreviewTable
                file={showMalformatted ? malformattedPrevizData : previzData}
                columns={
                    showMalformatted ? malformattedPrevizData.columns : columns
                }
                setColumns={setColumns}
                isNewTable={isNewTable}
                tableColumns={tableColumns}
            />
            <PreVizFooter
                isNewTable={isNewTable}
                action={action}
                setAction={setAction}
                isPrivate={isPrivate}
                isTableUpdateInProgress={isTableUpdateInProgress}
                setIsPrivate={setIsPrivate}
                newTableName={newTableName}
                cancel={cancel}
                onSubmit={onSubmit}
            />
            <SubmitModal
                submit={onImportClick}
                dialogText={t('importCSV.warning.malformattedText', {
                    malformattedRows: malformattedPrevizData.nbRows,
                    totalRows: malformattedPrevizData.fileLength,
                })}
                dialogTitle={t('importCSV.warning.malformattedTitle')}
                open={openMalformattedModal}
                setOpen={setOpenMalformattedModal}
            />
        </>
    );
};

export default React.memo(PreVizTable);
